import React from 'react';
import { UnsplashCredit } from '../common/UnsplashCredit';
import type { ExploreCard, ExplorePhoto } from '../../services/exploreApi';
import { tripLine } from '../../services/exploreApi';
import './ExploreSurface.css';

/**
 * The list beside the map (§1.5).
 *
 * **This is not a fallback.** §3 makes accessibility a guarantee: "the list
 * carries everything the map does and a screen reader opens list-first". So the
 * list is the primary rendering of the set and the map is a second view of it,
 * which is also why this component takes the cards rather than deriving them
 * from pins — the API returns both from one request precisely so the two
 * surfaces cannot disagree.
 *
 * The divider is placed from each card's OWN `inView` flag rather than by
 * slicing the array at a single count. A re-anchor merges two snapshots, and
 * the merged list's length no longer matches either one's count — slicing at
 * that point promotes a farther-afield card above the divider.
 */
/** A photo may be rendered ONLY with a credit that satisfies the licence: a
 *  named photographer, linked to their own profile, both halves UTM-tagged.
 *  The server suppresses incomplete rows today, but "the current backend does
 *  not send that" is not the same guarantee as "this cannot render it" — and
 *  the obligation is on the rendered page, not on the query. A partial payload
 *  fails CLOSED to no image: an uncredited photograph is a licence breach,
 *  while a missing photograph is a plainer card.
 *  An empty `photographer` makes `UnsplashCredit` return null outright, and an
 *  empty `unsplash_link` sends `photographerUrl` to the Unsplash HOMEPAGE — a
 *  credit that names a person and links somewhere else is not a credit. */
const creditable = (photo: ExplorePhoto | null): photo is ExplorePhoto =>
  !!photo && !!photo.url?.trim() && !!photo.photographer?.trim()
  && !!photo.unsplash_link?.trim();

const PhotoCredit: React.FC<{ card: ExploreCard }> = ({ card }) => {
  if (!creditable(card.photo)) return null;
  // `UnsplashCredit`, not a fourth spelling of it. Its own docstring says why:
  // "Centralising `photographerUrl` alone left every surface free to spell the
  // credit differently, and they did" — the hero linked only the photographer,
  // a day band said "Photo: X / Unsplash", the mobile one was not a link. The
  // rendering IS the compliance surface, Unsplash grants production limits
  // against a checklist naming the wording AND the links, and the reviewer
  // reads screenshots. I wrote a fourth spelling with no UTM parameters at all.
  //
  // The credit is a SIBLING of the image and not nested in the card's own
  // navigation: a link cannot contain a link, and a consolidated page credit
  // does not satisfy the terms — that was built, refuted and deleted.
  return (
    <figure className="explore-card__figure">
      <img src={card.photo.thumb_url} alt="" loading="lazy" />
      <figcaption className="explore-card__credit">
        <UnsplashCredit
          photographer={card.photo.photographer}
          profileLink={card.photo.unsplash_link}
        />
      </figcaption>
    </figure>
  );
};

const Card: React.FC<{
  card: ExploreCard;
  onOpen: (slug: string) => void;
  selected?: boolean;
  /** §1.5: a FARTHER-AFIELD card flies the map instead of opening the page.
   *  The place is outside the frame, so opening it straight away skips the
   *  map's own answer to "where is this?" — and the map is the surface. */
  onFly?: () => void;
}> = ({ card, onOpen, selected, onFly }) => (
  // §1.2: tapping a pin opens its CARD. `aria-current` says which one, so the
  // selection is announced rather than only tinted.
  <li
    className={`explore-card${selected ? ' explore-card--selected' : ''}`}
    aria-current={selected ? 'true' : undefined}
  >
    <PhotoCredit card={card} />
    <div className="explore-card__body">
      {/* The heading carries the link, so the card's photo credit can be its
          own link without nesting one inside another. */}
      <h3 className="explore-card__title">
        <button
          type="button"
          onClick={() => (onFly ? onFly() : onOpen(card.slug))}
        >
          {card.name}
        </button>
      </h3>
      {card.hook && <p className="explore-card__hook">{card.hook}</p>}
      {/* NO PERSON LINE (sheet page, slice E1, owner Sep 17). It read "Arnold
          Kim’s Chatham" under a title that already said "Chatham": the place
          named twice with an owner attached, above the one line that says
          anything about the trip. Its use count went with it — much of it
          was seeded, so it is not evidence of use (PHOTOS_ON_SHARED_PLANS,
          fact 06). The server still sends both; the row stops repeating
          them. */}
      {/* A guide-only destination speaks with the guide's voice rather than
          showing an empty card (§1.5). */}
      {!card.hook && card.guide && (
        <p className="explore-card__guide">
          VizDay’s guide to {card.name} · edition {card.guide.edition_number}
        </p>
      )}
      {/* The trip's own shape (Sep-7 field round, issue 3: "Current
          descriptions/meta in list are terrible and not engaging!"). The row
          described the RECORD and nothing else; it now leads with the trip and
          keeps the reason line below, unchanged. */}
      {tripLine(card.trip) && (
        <p className="explore-card__trip">{tripLine(card.trip)}</p>
      )}
      {card.reason && (
        <p className="explore-card__reason">{card.reason.text}</p>
      )}
    </div>
  </li>
);

export const ListSheet: React.FC<{
  cards: (ExploreCard & { inView: boolean })[];
  onOpen: (slug: string) => void;
  selectedSlug?: string | null;
  onFly?: (lat: number, lng: number) => void;
}> = ({ cards, onOpen, selectedSlug, onFly }) => {
  const inView = cards.filter((c) => c.inView);
  const farther = cards.filter((c) => !c.inView);
  return (
    <section className="explore-list" aria-label="Places in view">
      {/* Nothing in view is an ANSWER, and §1.8 asks filters to "narrow what is
          in view and say so". An empty `<ul>` says nothing: with a filter that
          matches nothing here, the reader saw an empty map above a heading
          reading "Farther afield" and had to infer the rest. Observed in a
          browser under `A week+`, which the Northeast has none of in frame. */}
      {inView.length === 0 && farther.length > 0 && (
        <p className="explore-list__none">Nothing in view matches.</p>
      )}
      {inView.length > 0 && (
        <ul className="explore-list__items">
          {inView.map((card) => (
            <Card key={card.slug} card={card} onOpen={onOpen}
                  selected={card.slug === selectedSlug} />
          ))}
        </ul>
      )}
      {farther.length > 0 && (
        <>
          {/* A real heading, not a decorative rule: the divider is how a reader
              knows the list has left the viewport, and a screen reader needs to
              hear that as much as a sighted reader needs to see it. */}
          <h2 className="explore-list__divider">Farther afield</h2>
          <ul className="explore-list__items">
            {farther.map((card) => (
              <Card key={card.slug} card={card} onOpen={onOpen}
                selected={card.slug === selectedSlug}
                // `Destination.latitude/longitude` are nullable, so a card
                // outside the frame may have nowhere to fly TO. It opens its
                // page instead — the spec's interaction needs a coordinate.
                onFly={onFly && card.lat !== null && card.lng !== null
                  ? () => onFly(card.lat as number, card.lng as number)
                  : undefined} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
